import { CameraIcon, ChevronRightIcon, MicIcon, TextIcon } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { obtenerIniciales } from "@/lib/iniciales";
import type { ItemDeTerreno } from "@/lib/terreno";
import { cn } from "@/lib/utils";

import type { ConteoDeRegistros } from "./aguasTerreno";
import { BotonDescartarDeTerreno } from "./DescarteDeTerreno";
import { TarjetaDeRegistroDeTerreno } from "./TarjetaDeRegistroDeTerreno";

/* Las dos piezas que comparten las filas con registros de terreno de las
   pestañas de aguas y de maquinaria: el contador que abre el bloque y el bloque
   mismo. Son de LECTURA — el registro propone y nunca copia nada a la fila. */

function Contador({ icono, cuantos }: { icono: React.ReactNode; cuantos: number }) {
  return (
    <span className="inline-flex items-center gap-1">
      {icono}
      <span className="font-mono">{cuantos}</span>
    </span>
  );
}

interface ContadorDeRegistrosProps {
  conteo: ConteoDeRegistros;
  abierto: boolean;
  /** "Registro de terreno de la fuente 1": el nombre accesible del botón. */
  etiqueta: string;
  onAlternar: () => void;
}

/** Fotos, audios y textos que trajo la visita; al tocarlo abre el registro. */
export function ContadorDeRegistros({ conteo, abierto, etiqueta, onAlternar }: ContadorDeRegistrosProps) {
  return (
    <button
      type="button"
      aria-expanded={abierto}
      aria-label={etiqueta}
      onClick={onAlternar}
      className="text-ink-body hover:text-ink inline-flex items-center gap-2 text-[0.71875rem] font-bold"
    >
      {conteo.fotos > 0 ? (
        <Contador icono={<CameraIcon className="size-3.5" aria-hidden="true" />} cuantos={conteo.fotos} />
      ) : null}
      {conteo.audios > 0 ? (
        <Contador icono={<MicIcon className="size-3.5" aria-hidden="true" />} cuantos={conteo.audios} />
      ) : null}
      {conteo.textos > 0 ? (
        <Contador icono={<TextIcon className="size-3.5" aria-hidden="true" />} cuantos={conteo.textos} />
      ) : null}
      <ChevronRightIcon
        className={cn("text-ink-hint size-3.5 transition-transform", abierto && "rotate-90")}
        aria-hidden="true"
      />
    </button>
  );
}

interface BloqueDeRegistrosProps {
  informeId: number;
  item: ItemDeTerreno;
  /** Cómo se llama la fila en el encabezado del registro. */
  titulo: string;
  /** Quien fue a terreno: sus iniciales encabezan el registro, como en el diseño. */
  encargado: string | null;
  onAbrirFoto: (uuid: string) => void;
}

/** "Registro de terreno · {nombre}": las tarjetas de los registros de UN ítem. */
export function BloqueDeRegistros({ informeId, item, titulo, encargado, onAbrirFoto }: BloqueDeRegistrosProps) {
  return (
    <div className="border-border bg-superficie-sutil mx-3 mb-3 flex flex-col gap-2 rounded-xl border-[1.5px] p-2.5">
      <div className="flex items-center gap-2">
        <Avatar className="size-5">
          <AvatarFallback className="bg-success-soft-foreground text-primary-foreground text-[0.53125rem] font-extrabold">
            {encargado ? obtenerIniciales(encargado) : "?"}
          </AvatarFallback>
        </Avatar>
        <span className="text-ink text-[0.71875rem] font-bold">Registro de terreno</span>
        <span className="text-ink-muted text-[0.6875rem] font-semibold">· {titulo}</span>
        {item.nuevo && (
          <span className="ml-auto">
            <BotonDescartarDeTerreno
              uuid={item.referencia.slice(item.referencia.lastIndexOf(":") + 1)}
              etiqueta={titulo}
            />
          </span>
        )}
      </div>
      {item.registros.map((registro, indice) => (
        <TarjetaDeRegistroDeTerreno
          key={registro.uuid}
          informeId={informeId}
          registro={registro}
          indice={indice}
          de={titulo}
          onAbrirFoto={onAbrirFoto}
        />
      ))}
    </div>
  );
}
